import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, User as UserIcon, Phone, Mail, Car, PoundSterling, Calendar,
  ShieldCheck, BellPlus, AlertTriangle, CheckCircle2, Plus, Search,
  FileText, Loader2, ShieldOff, Sparkles, MapPin, Clock,
} from "lucide-react";
import { api } from "@/lib/api";

// ---------------------------------------------------------------------------
// Customer profile page — "the workshop's customer brain"
// Keyed by registration plate so it survives phone/email changes.
// Pulls /api/customers/by-reg/:reg + /api/vehicles/:reg/mot in parallel.
// ---------------------------------------------------------------------------
export default function CustomerProfilePage() {
  const { reg } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [mot, setMot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.get(`/customers/by-reg/${encodeURIComponent(reg)}`),
      api.get(`/vehicles/${encodeURIComponent(reg)}/mot`).catch(() => ({ data: { configured: false } })),
    ])
      .then(([p, m]) => {
        if (cancelled) return;
        setProfile(p.data);
        setMot(m.data);
      })
      .catch((e) => {
        if (cancelled) return;
        toast.error(e?.response?.data?.detail || "Couldn't load this customer");
        navigate(-1);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [reg, navigate]);

  const startNewJob = async () => {
    if (!profile || creating) return;
    setCreating(true);
    try {
      const r = await api.post("/jobs", {
        customer_name: profile.customer.name,
        customer_phone: profile.customer.phone,
        customer_email: profile.customer.email,
        vehicle_make: profile.vehicle.make,
        vehicle_model: profile.vehicle.model,
        vehicle_year: profile.vehicle.year,
        vehicle_reg: profile.vehicle.reg,
      });
      toast.success("New job created");
      navigate(`/jobs/${r.data.id}`);
    } catch {
      toast.error("Couldn't create job");
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center text-muted-foreground" data-testid="customer-loading">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }
  if (!profile) return null;

  const { customer, vehicle, lifetime, consent, upcoming_reminders: reminders, jobs } = profile;
  const motOk = mot?.configured && mot?.found && mot?.mot_expiry;

  return (
    <div className="min-h-screen bg-background" data-testid="customer-profile">
      <header className="border-b border-border bg-card sticky top-0 z-10 backdrop-blur">
        <div className="max-w-6xl mx-auto px-5 py-3 flex items-center justify-between gap-3">
          <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="customer-back">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="font-mono-plex text-xs text-muted-foreground tracking-wider uppercase">Customer brain</div>
          <button onClick={startNewJob} disabled={creating} className="btn-primary text-sm" data-testid="customer-new-job-btn">
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Start new job
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 py-8 space-y-8">
        {/* Hero: who + what they drive */}
        <section className="grid lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 card-default p-6">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-md bg-primary/15 text-primary grid place-items-center text-xl font-bold shrink-0">
                {(customer.name || "?").trim().charAt(0).toUpperCase() || "?"}
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight leading-tight" data-testid="customer-name">
                  {customer.name || <span className="text-muted-foreground">No name on file</span>}
                </h1>
                <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-muted-foreground">
                  {customer.phone && (
                    <a href={`tel:${customer.phone}`} className="hover:text-foreground inline-flex items-center gap-1.5" data-testid="customer-phone">
                      <Phone className="w-3.5 h-3.5" /> {customer.phone}
                    </a>
                  )}
                  {customer.email && (
                    <a href={`mailto:${customer.email}`} className="hover:text-foreground inline-flex items-center gap-1.5 truncate max-w-full" data-testid="customer-email">
                      <Mail className="w-3.5 h-3.5 shrink-0" /> <span className="truncate">{customer.email}</span>
                    </a>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-5 rounded-lg border border-border bg-muted/20 p-4 flex items-center gap-3" data-testid="customer-vehicle">
              <Car className="w-5 h-5 text-primary shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-lg leading-tight">
                  {[vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(" ") || "Vehicle"}
                </div>
                <div className="font-mono-plex tracking-widest text-xs text-muted-foreground mt-0.5">{vehicle.reg}</div>
              </div>
              {motOk && (
                <div className="text-right shrink-0">
                  <div className="label-overline">MOT expires</div>
                  <div className="font-display font-bold text-sm" data-testid="customer-mot">{new Date(mot.mot_expiry).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</div>
                </div>
              )}
              {!motOk && mot?.configured && mot?.found === false && (
                <span className="text-xs text-muted-foreground">No MOT history</span>
              )}
            </div>
          </div>

          {/* Lifetime KPIs */}
          <div className="card-default p-6 space-y-4 bg-gradient-to-br from-primary/5 to-transparent" data-testid="lifetime-kpis">
            <div className="label-overline text-primary">Lifetime with us</div>
            <div className="flex items-baseline gap-3">
              <div className="font-display font-black text-5xl tracking-tight text-primary" data-testid="lifetime-spend">
                £{Math.round(lifetime.spend).toLocaleString()}
              </div>
              <div className="text-xs text-muted-foreground">spent across {lifetime.visits} {lifetime.visits === 1 ? "visit" : "visits"}</div>
            </div>
            <div className="grid grid-cols-2 gap-3 pt-2">
              <Mini label="Conversion" value={`${lifetime.conversion_rate}%`} />
              <Mini label="Items approved" value={`${lifetime.items_approved}/${lifetime.items_total}`} />
              <Mini label="Red recovered" value={lifetime.red_recovered} />
              <Mini label="First visit" value={lifetime.first_visit ? new Date(lifetime.first_visit).toLocaleDateString("en-GB", { month: "short", year: "numeric" }) : "—"} />
            </div>
          </div>
        </section>

        {/* Consent + Reminders row */}
        <section className="grid md:grid-cols-2 gap-5">
          <ConsentCard consent={consent} />
          <RemindersCard reminders={reminders} />
        </section>

        {/* Job timeline */}
        <section data-testid="customer-jobs">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div>
              <div className="label-overline">Job history</div>
              <h2 className="font-display font-bold text-xl">{lifetime.visits} {lifetime.visits === 1 ? "visit" : "visits"} — newest first</h2>
            </div>
          </div>
          <ol className="space-y-3">
            {jobs.map((j, i) => (
              <li key={j.job_id} data-testid={`job-row-${i}`}>
                <Link to={`/jobs/${j.job_id}`} className="block card-default p-4 hover:border-primary/40 transition-colors">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <div className={`w-10 h-10 rounded-md grid place-items-center shrink-0 ${
                        j.status === "completed" ? "bg-success/15 text-success" :
                        j.status === "approved" ? "bg-success/15 text-success" :
                        j.status === "rejected" ? "bg-destructive/15 text-destructive" :
                        j.status === "partial" ? "bg-warning/15 text-warning" :
                        "bg-muted text-muted-foreground"
                      }`}>
                        <FileText className="w-5 h-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="font-display font-bold leading-tight truncate">
                            {j.work_description || `Visit #${jobs.length - i}`}
                          </div>
                          <span className={`label-overline px-1.5 py-0.5 rounded ${
                            j.status === "completed" || j.status === "approved" ? "bg-success/15 text-success" :
                            j.status === "rejected" ? "bg-destructive/15 text-destructive" :
                            j.status === "partial" ? "bg-warning/15 text-warning" :
                            "bg-muted text-muted-foreground"
                          }`}>{j.status || "draft"}</span>
                        </div>
                        <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                          <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {j.created_at ? new Date(j.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—"}</span>
                          <span>{j.approved_count}/{j.item_count} items approved</span>
                          {j.red_count > 0 && <span className="text-destructive">{j.red_count} red flagged</span>}
                        </div>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-display font-black text-lg">£{Math.round(j.approved_total).toLocaleString()}</div>
                      <div className="text-[11px] text-muted-foreground">of £{Math.round(j.subtotal).toLocaleString()} quoted</div>
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ol>
        </section>
      </main>
    </div>
  );
}

function Mini({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
      <div className="font-display font-bold text-sm mt-0.5">{value}</div>
    </div>
  );
}

function ConsentCard({ consent }) {
  if (!consent) {
    return (
      <div className="card-default p-5 flex items-start gap-3" data-testid="consent-none">
        <ShieldOff className="w-5 h-5 text-muted-foreground mt-0.5" />
        <div>
          <div className="label-overline">GDPR consent</div>
          <div className="font-display font-bold text-base mt-0.5">Not yet captured</div>
          <p className="text-xs text-muted-foreground mt-1">When this customer fills in a pre-work questionnaire, their consent record will land here.</p>
        </div>
      </div>
    );
  }
  return (
    <div className="card-default p-5" data-testid="consent-card">
      <div className="flex items-center gap-2 label-overline text-primary mb-2">
        <ShieldCheck className="w-3.5 h-3.5" /> GDPR consent
      </div>
      <ul className="space-y-1.5 text-sm">
        <li className="flex items-center gap-2">
          {consent.data_processing
            ? <CheckCircle2 className="w-4 h-4 text-success shrink-0" />
            : <AlertTriangle className="w-4 h-4 text-warning shrink-0" />}
          <span>Data processing — <span className="font-semibold">{consent.data_processing ? "agreed" : "NOT agreed"}</span></span>
        </li>
        <li className="flex items-center gap-2">
          {consent.marketing
            ? <Mail className="w-4 h-4 text-primary shrink-0" />
            : <span className="w-4 h-4 rounded-full border border-muted-foreground/40 shrink-0" />}
          <span>Marketing — <span className="text-muted-foreground">{consent.marketing ? "opted in (MOT/service reminders OK)" : "opted out"}</span></span>
        </li>
      </ul>
      <div className="text-[11px] text-muted-foreground mt-3">
        Captured {new Date(consent.consent_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })} · audit trail
      </div>
    </div>
  );
}

function RemindersCard({ reminders }) {
  if (!reminders || reminders.length === 0) {
    return (
      <div className="card-default p-5 flex items-start gap-3" data-testid="reminders-none">
        <BellPlus className="w-5 h-5 text-muted-foreground mt-0.5" />
        <div>
          <div className="label-overline">Upcoming reminders</div>
          <div className="font-display font-bold text-base mt-0.5">None scheduled</div>
          <p className="text-xs text-muted-foreground mt-1">Declined amber items the customer asks us to remind them about will appear here.</p>
        </div>
      </div>
    );
  }
  return (
    <div className="card-default p-5 bg-warning/5 border-warning/30" data-testid="reminders-card">
      <div className="flex items-center gap-2 label-overline text-warning mb-3">
        <BellPlus className="w-3.5 h-3.5" /> {reminders.length} reminder{reminders.length === 1 ? "" : "s"} scheduled
      </div>
      <ul className="space-y-2.5">
        {reminders.slice(0, 4).map((r, i) => (
          <li key={`${r.job_id}-${r.item_id}`} className="flex items-center justify-between gap-3 text-sm" data-testid={`reminder-${i}`}>
            <div className="min-w-0 flex-1">
              <div className="font-semibold truncate">{r.name}</div>
              <div className="text-[11px] text-muted-foreground">
                {r.first_sent_at ? "Sent · awaiting" : `Fires ${new Date(r.scheduled_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`}
              </div>
            </div>
            <div className="font-mono-plex font-bold text-sm shrink-0">£{Math.round(r.value)}</div>
          </li>
        ))}
      </ul>
      {reminders.length > 4 && <div className="text-center text-xs text-muted-foreground mt-2">+ {reminders.length - 4} more</div>}
    </div>
  );
}
